// The one community-chat surface: pinned banner, message feed, gates and
// composer, extracted from ChatDrawer so the onsite drawer and the
// fullscreen race theater render the SAME state from the same provider —
// one adapter, one socket, one unread/reply stream.

import { Ban, Clock, Lock, Pin } from "lucide-react";
import { type ReactNode, useState } from "react";

import { useSession } from "@/session/SessionProvider";

import { ChatComposer } from "./ChatComposer";
import { useChat } from "./ChatProvider";
import { MessageFeed } from "./MessageFeed";
import type { ChatMessage } from "./types";

export function ChatSurface() {
  const chat = useChat();
  const { session } = useSession();

  const disabledHint = composerHint({
    authenticated: Boolean(session),
    banned: chat.snapshot.banned,
    canSend: chat.snapshot.canSend,
    rateLimitSeconds: chat.snapshot.rateLimitSeconds,
    status: chat.snapshot.status,
  });
  const composerDisabled = Boolean(disabledHint);

  return (
    <>
      {chat.snapshot.pinnedMessage ? (
        <PinnedBanner message={chat.snapshot.pinnedMessage} />
      ) : null}
      <MessageFeed
        onLoadOlder={() => void chat.loadOlder()}
        onReact={
          session
            ? (messageId, emoji) => void chat.react(messageId, emoji)
            : undefined
        }
        onReply={session ? chat.setReplyTarget : undefined}
        snapshot={chat.snapshot}
      />
      {session && chat.snapshot.banned ? (
        <ChatGateCard
          icon={<Ban aria-hidden />}
          message="You have been banned from chat"
          variant="danger"
        />
      ) : session && chat.snapshot.wagerGate ? (
        <WagerGateCard gate={chat.snapshot.wagerGate} />
      ) : session && chat.snapshot.rateLimitSeconds > 0 ? (
        <ChatGateCard
          badge={`${chat.snapshot.rateLimitSeconds}s`}
          icon={<Clock aria-hidden />}
          message="Wait before sending"
          title="Slow down!"
          variant="info"
        />
      ) : session ? (
        <ChatComposer
          disabled={composerDisabled}
          disabledHint={disabledHint}
          error={chat.actionError}
          onCancelReply={() => chat.setReplyTarget(null)}
          onSend={chat.sendMessage}
          replyTarget={chat.replyTarget}
          sending={chat.sending}
        />
      ) : (
        <div className="chat-drawer__signin">
          <p>Sign in to chat</p>
        </div>
      )}
    </>
  );
}

function WagerGateCard({
  gate,
}: {
  gate: { wagered: number; threshold: number; percentage: number };
}) {
  const percentage = Math.max(0, Math.min(100, gate.percentage));
  return (
    <div className="chat-wager-gate" role="status">
      <div>
        <span>
          <strong>Chat Locked</strong>
          <small>Spend to unlock chat</small>
        </span>
        <div className="chat-wager-gate__progress" aria-hidden>
          <i style={{ width: `${percentage}%` }} />
        </div>
        <p>
          ${Math.max(0, Math.round(gate.wagered)).toLocaleString()} / $
          {Math.max(0, Math.round(gate.threshold)).toLocaleString()}
        </p>
        <Lock aria-hidden />
      </div>
    </div>
  );
}

function PinnedBanner({ message }: { message: ChatMessage }) {
  const [expanded, setExpanded] = useState(false);

  function revealMessage() {
    const target = document.querySelector<HTMLElement>(
      `[data-message-id="${message.id}"]`,
    );
    if (!target) {
      setExpanded((value) => !value);
      return;
    }
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    target.classList.remove("chat-message--jumped");
    void target.offsetWidth;
    target.classList.add("chat-message--jumped");
    window.setTimeout(
      () => target.classList.remove("chat-message--jumped"),
      2600,
    );
  }

  return (
    <div className="chat-drawer__pinned">
      <Pin aria-hidden />
      <button aria-expanded={expanded} onClick={revealMessage} type="button">
        <strong>
          Pinned <span aria-hidden>·</span> {message.displayName}
        </strong>
        <small className={expanded ? "is-expanded" : ""}>{message.text}</small>
      </button>
    </div>
  );
}

function ChatGateCard({
  badge,
  icon,
  message,
  title,
  variant,
}: {
  badge?: string;
  icon: ReactNode;
  message: string;
  title?: string;
  variant: "danger" | "info";
}) {
  return (
    <div className={`chat-gate-card is-${variant}`} role="status">
      <div>
        {icon}
        <span>
          {title ? <strong>{title}</strong> : null}
          <small>{message}</small>
        </span>
        {badge ? <b>{badge}</b> : null}
      </div>
    </div>
  );
}

function composerHint({
  authenticated,
  banned,
  canSend,
  rateLimitSeconds,
  status,
}: {
  authenticated: boolean;
  banned: boolean;
  canSend: boolean;
  rateLimitSeconds: number;
  status: "unavailable" | "connecting" | "connected" | "error";
}) {
  if (!authenticated) return "Sign in to chat.";
  if (banned) return "You have been banned from chat.";
  if (rateLimitSeconds > 0) return `Wait ${rateLimitSeconds}s before sending.`;
  if (status === "unavailable")
    return "Chat is awaiting its Channels connection.";
  if (status === "connecting") return "Chat is read-only while it connects.";
  if (status === "error") return "Chat is temporarily read-only.";
  if (!canSend) return "Chat is read-only while you catch up.";
  return undefined;
}
